import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import RuleIcon from '@mui/icons-material/Rule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import AppService from '../../../../Configurations/Appservice';
import { useDispatch } from 'react-redux';
import {
  showLoader,
  dismissLoader,
  showSuccessMessage,
} from '../../../../LayoutContainers/AppLayout/redux/AppSlice';
import RuleFormDialog from './RuleFormDialog';

/**
 * Required Document Rules Management Page
 * Lists all required document rules with CRUD operations
 * Uses mock data during UI-First phase
 */
const RequiredRulesPage = () => {
  const dispatch = useDispatch();
  const [rules, setRules] = useState([]);
  const [filteredRules, setFilteredRules] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [organizationCategories, setOrganizationCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Load data from API
  useEffect(() => {
    loadRules();
    loadDocumentTypes();
    loadOrganizationCategories();
  }, []);

  const loadRules = () => {
    setLoading(true);
    AppService.get('api/required-rules')
      .then((response) => {
        setRules(response || []);
      })
      .catch((error) => {
        console.error('Failed to load rules', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const loadDocumentTypes = () => {
    AppService.get('api/organization-document-types')
      .then((response) => {
        setDocumentTypes(response || []);
      })
      .catch((error) => {
        console.error('Failed to load document types', error);
      });
  };

  const loadOrganizationCategories = () => {
    AppService.get('api/organisations/categories')
      .then((response) => {
        setOrganizationCategories(response || []);
      })
      .catch((error) => {
        console.error('Failed to load organization categories', error);
      });
  };

  // Filter rules based on search term
  useEffect(() => {
    let filtered = rules;

    if (searchTerm) {
      filtered = filtered.filter(r =>
        r.documentTypeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.documentTypeCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.missingMessage?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredRules(filtered);
  }, [searchTerm, rules]);

  const handleAddClick = () => {
    setSelectedRule(null);
    setDialogOpen(true);
  };

  const handleEditClick = (rule) => {
    setSelectedRule(rule);
    setDialogOpen(true);
  };

  const handleDeleteClick = (rule) => {
    setSelectedRule(rule);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirmClose = () => {
    setDeleteConfirmOpen(false);
    setSelectedRule(null);
  };

  const handleDelete = () => {
    dispatch(showLoader());
    AppService.delete(`api/required-rules/${selectedRule.id}`)
      .then(() => {
        dispatch(showSuccessMessage('Rule deleted successfully'));
        loadRules();
        setDeleteConfirmOpen(false);
        setSelectedRule(null);
      })
      .catch((error) => {
        console.error('Failed to delete rule', error);
      })
      .finally(() => {
        dispatch(dismissLoader());
      });
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedRule(null);
  };

  const handleSave = async (ruleData) => {
    dispatch(showLoader());
    const requestData = {
      documentTypeId: ruleData.documentTypeId,
      organizationCategory: ruleData.organizationCategory,
      conditions: ruleData.conditions || '',
      isMandatory: ruleData.isMandatory,
      missingMessage: ruleData.missingMessage,
      isActive: ruleData.isActive,
    };
    if (selectedRule) {
      // Update existing
      AppService.put(`api/required-rules/${selectedRule.id}`, requestData)
        .then(() => {
          dispatch(showSuccessMessage('Rule updated successfully'));
          loadRules();
          handleDialogClose();
        })
        .catch((error) => {
          console.error('Failed to update rule', error);
        })
        .finally(() => {
          dispatch(dismissLoader());
        });
    } else {
      // Add new
      AppService.post('api/required-rules', requestData)
        .then(() => {
          dispatch(showSuccessMessage('Rule created successfully'));
          loadRules();
          handleDialogClose();
        })
        .catch((error) => {
          console.error('Failed to create rule', error);
        })
        .finally(() => {
          dispatch(dismissLoader());
        });
    }
  };

  const getOrgCategoryLabel = (orgCategory) => {
    if (!orgCategory || orgCategory === 'ALL') return 'All';
    const category = organizationCategories.find(c => c.code === orgCategory);
    return category?.label || category?.name || orgCategory;
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RuleIcon color="primary" />
            Required Document Rules
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure which documents are required based on organization profile conditions
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
        >
          Add Rule
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <TextField
          size="small"
          placeholder="Search rules..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            )
          }}
          sx={{ width: 300 }}
        />
      </Box>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Document Type</TableCell>
              <TableCell>Organization Category</TableCell>
              <TableCell>Conditions</TableCell>
              <TableCell align="center">Mandatory</TableCell>
              <TableCell>Missing Message</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  Loading...
                </TableCell>
              </TableRow>
            ) : filteredRules.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  No rules found
                </TableCell>
              </TableRow>
            ) : (
              filteredRules.map((rule) => (
                <TableRow key={rule.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {rule.documentTypeName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {rule.documentTypeCode}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getOrgCategoryLabel(rule.organizationCategory)}
                      size="small"
                      variant="outlined"
                      color={rule.organizationCategory && rule.organizationCategory !== 'ALL' ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {rule.conditions ? (
                      <Tooltip title={rule.conditions}>
                        <Chip
                          label={rule.conditions.length > 30 ? rule.conditions.substring(0, 30) + '...' : rule.conditions}
                          size="small"
                          color="info"
                          variant="outlined"
                          icon={<InfoIcon />}
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No conditions
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {rule.isMandatory ? (
                      <Chip
                        label="Required"
                        size="small"
                        color="error"
                        icon={<CheckCircleIcon />}
                      />
                    ) : (
                      <Chip
                        label="Recommended"
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <Tooltip title={rule.missingMessage}>
                        <span>{rule.missingMessage}</span>
                      </Tooltip>
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={rule.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={rule.isActive ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEditClick(rule)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDeleteClick(rule)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary */}
      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Total: {filteredRules.length} rules
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Mandatory: {filteredRules.filter(r => r.isMandatory).length}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          With Conditions: {filteredRules.filter(r => r.conditions).length}
        </Typography>
      </Box>

      {/* Form Dialog */}
      <RuleFormDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSave={handleSave}
        rule={selectedRule}
        documentTypes={documentTypes}
        organizationCategories={organizationCategories}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteConfirmClose}>
        <DialogTitle>Delete Rule</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this rule for "{selectedRule?.documentTypeName}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteConfirmClose}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RequiredRulesPage;
